import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { ArrowRight, BookOpen, CheckCircle2 } from 'lucide-react';
import Navbar from '../components/Navbar';

function isActiveSubscriber(user) {
  if (!user) return false;
  if (user.role === 'admin') return true;
  if (user.subscription_status === 'active') return true;
  if (user.institution_id) return true; // institution-backed access — server enforces final check
  return false;
}

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Parse ?redirect=/cases/<id> if present
  const searchParams = new URLSearchParams(location.search);
  const redirectTo = searchParams.get('redirect') || '';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const userData = await login(email, password);
      toast.success('Login successful!');

      // Admin always goes to admin dashboard
      if (userData?.role === 'admin') {
        navigate('/admin');
        return;
      }

      // If user came from a specific case (redirect=/cases/<id>)
      if (redirectTo) {
        if (isActiveSubscriber(userData)) {
          navigate(redirectTo);
        } else {
          // Pass intended case so subscription page can show context
          navigate(`/subscription?redirect=${encodeURIComponent(redirectTo)}`);
        }
        return;
      }

      // Default: subscribers go to library, non-subscribers go to subscription page
      if (isActiveSubscriber(userData)) {
        navigate('/cases');
      } else {
        navigate('/subscription');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  // Build sign-up link that carries the redirect param forward
  const signupHref = redirectTo
    ? `/register?redirect=${encodeURIComponent(redirectTo)}`
    : '/register';

  return (
    <div className="min-h-screen bg-slate-50" data-testid="login-page">
      <Navbar />
      <div className="pt-24 pb-16 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <h1 className="text-4xl md:text-5xl font-serif font-bold tracking-tight text-slate-900" data-testid="login-page-title">
              Sign in to The Case Verse
            </h1>
            <p className="mt-2 text-slate-600">Access premium case studies, teaching notes, and student worksheets.</p>
            {redirectTo && (
              <p className="mt-3 inline-block text-xs bg-orange-50 border border-orange-200 text-orange-800 rounded-full px-3 py-1" data-testid="redirect-notice">
                You&apos;ll continue where you left off after signing in.
              </p>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-stretch">
            {/* LEFT — Already registered */}
            <div className="bg-white p-8 rounded-xl border border-slate-200 shadow-sm" data-testid="login-card">
              <h2 className="text-xl font-serif font-semibold text-slate-900 mb-1">Already registered?</h2>
              <p className="text-sm text-slate-500 mb-6">Enter your credentials to sign in to your account.</p>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-2">Email Address</label>
                  <input
                    id="email"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full px-4 py-2 border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent"
                    placeholder="you@example.com"
                    data-testid="email-input"
                    autoComplete="email"
                  />
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label htmlFor="password" className="block text-sm font-medium text-slate-700">Password</label>
                    <Link to="/forgot-password" className="text-xs text-orange-600 hover:text-orange-700 font-medium" data-testid="forgot-password-link">
                      Forgot password?
                    </Link>
                  </div>
                  <input
                    id="password"
                    type="password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full px-4 py-2 border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent"
                    placeholder="Enter your password"
                    data-testid="password-input"
                    autoComplete="current-password"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-3 bg-slate-900 text-white rounded hover:bg-slate-800 transition-all disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                  data-testid="login-submit-button"
                >
                  {loading ? 'Signing in...' : 'Sign In'}
                </button>
              </form>
            </div>

            {/* RIGHT — Not registered yet */}
            <div className="bg-slate-900 text-white p-8 rounded-xl shadow-sm flex flex-col" data-testid="signup-card">
              <div className="flex items-center gap-2 text-orange-400 mb-2">
                <BookOpen className="w-5 h-5" />
                <span className="text-xs font-mono uppercase tracking-wider">New to The Case Verse?</span>
              </div>
              <h2 className="text-2xl font-serif font-semibold mb-3">Not registered yet? Sign up here</h2>
              <p className="text-slate-300 text-sm mb-6">
                Create your free account to start exploring our premium catalogue of business case studies, teaching notes, and student worksheets.
              </p>

              <ul className="space-y-3 mb-8 text-sm">
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-orange-400 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-200">Search the full catalogue of premium cases</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-orange-400 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-200">Instructor notes & student worksheets included</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-orange-400 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-200">Flexible academic & corporate subscriptions</span>
                </li>
              </ul>

              <Link
                to={signupHref}
                className="mt-auto inline-flex items-center justify-center gap-2 px-6 py-3 bg-orange-600 text-white rounded hover:bg-orange-700 transition-all font-medium"
                data-testid="register-link"
              >
                Sign Up Here <ArrowRight className="w-4 h-4" />
              </Link>

              <div className="mt-4 pt-4 border-t border-slate-700 text-center">
                <p className="text-xs text-slate-400 mb-2">Have an institute-issued access ID?</p>
                <Link
                  to="/redeem"
                  className="inline-flex items-center gap-1 text-orange-400 hover:text-orange-300 text-sm font-medium"
                  data-testid="redeem-access-id-link"
                >
                  Redeem access ID <ArrowRight className="w-3 h-3" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
